import { useEffect, ComponentType } from "react";
import { Loader } from "@mantine/core";
import {
  ObservationRecorder,
  useObsScreenRecording,
} from "@deep-consulting-solutions/observation-ui";
import type { UseObsScreenRecordingResult } from "@deep-consulting-solutions/observation-ui";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useAuthStore } from "./store";
import { useTaskStore } from "./store/task.store";
import { setAuthToken } from "./lib";
import { getDefaultRoute, isRouteAllowed } from "./data/taskNavigation";
import { useGcpSessionRecording } from "./hooks/useGcpSessionRecording";
import { usePageVisitTracking } from "./hooks/usePageVisitTracking";
import { AppLayout } from "./components/layout";
import FloatingObservationTrigger from "./components/layout/FloatingObservationTrigger";
import { ErrorBoundary } from "./components/common";
import { TaskGuard } from "./components/common/TaskGuard";
import SessionTimeoutModal from "./components/common/SessionTimeoutModal";
import { useSessionTimeout } from "./hooks/useSessionTimeout";
import TaskSelection from "./pages/TaskSelection";
import PortalReportView from "./pages/report/PortalReportView";
import { usePortalAuthStore } from "./store/portalAuth.store";
import { PortalLayout } from "./components/portal/PortalLayout";
import { PortalRegisterPage } from "./pages/portal/PortalRegisterPage";
import { PortalLoginPage } from "./pages/portal/PortalLoginPage";
import { PortalPasswordResetPage } from "./pages/portal/PortalPasswordResetPage";
import { PortalDashboardPage } from "./pages/portal/PortalDashboardPage";
import { PortalReportViewPage } from "./pages/portal/PortalReportViewPage";
import { PortalAccountPage } from "./pages/portal/PortalAccountPage";
import { PortalMagicAccessPage } from "./pages/portal/PortalMagicAccessPage";
import { PortalRequestSuppliesPage } from "./pages/portal/PortalRequestSuppliesPage";
import { PortalContactHelpPage } from "./pages/portal/PortalContactHelpPage";
import PortalSelector from "./pages/PortalSelector";
import RenderReportPage from "./pages/internal/RenderReportPage";
import RenderBillingReportPage from "./pages/internal/RenderBillingReportPage";
import RenderTcTechnicalSummaryPage from "./pages/internal/RenderTcTechnicalSummaryPage";

import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ForceChangePassword from "./pages/ForceChangePassword";
import "./lib/observationTool";
import AccountSettings from "./pages/AccountSettings";
import CasesList from "./pages/cases/CasesList";
import CaseDetailV2 from "./pages/cases/CaseDetailV2";
import AdminPage from "./pages/admin/AdminPage";
import LegacyCasesGuard from "./components/admin/legacy-cases/LegacyCasesGuard";
import LegacyCasesPage from "./pages/admin/legacy-cases/LegacyCasesPage";
import LegacyCaseDetailPage from "./pages/admin/legacy-cases/LegacyCaseDetailPage";
import LegacyReportReviewPage from "./pages/admin/legacy-reports/LegacyReportReviewPage";
import LegacyReportReviewGuard from "./components/admin/legacy-reports/LegacyReportReviewGuard";
import {
  canManageLegacyCases,
  canReviewLegacyReports,
} from "./utils/legacyCaseAccess";
import FacilitiesAdminPage from "./pages/admin/organization/FacilitiesAdminPage";
import UsersAdminPage from "./pages/admin/organization/UsersAdminPage";
import RolesAdminPage from "./pages/admin/organization/RolesAdminPage";
import GroupsAdminPage from "./pages/admin/organization/GroupsAdminPage";
import PermissionsAdminPage from "./pages/admin/organization/PermissionsAdminPage";
import GroupPermissionsAdminPage from "./pages/admin/organization/GroupPermissionsAdminPage";
import RolePermissionsAdminPage from "./pages/admin/organization/RolePermissionsAdminPage";
import ProceduresAdminPage from "./pages/admin/medical-reference/ProceduresAdminPage";
import CollectionMethodsAdminPage from "./pages/admin/medical-reference/CollectionMethodsAdminPage";
import SourcesAdminPage from "./pages/admin/medical-reference/SourcesAdminPage";
import SourceSubsitesAdminPage from "./pages/admin/medical-reference/SourceSubsitesAdminPage";
import SourceDefaultStainsAdminPage from "./pages/admin/medical-reference/SourceDefaultStainsAdminPage";
import StainsAdminPage from "./pages/admin/medical-reference/StainsAdminPage";
import PanelsAdminPage from "./pages/admin/medical-reference/PanelsAdminPage";
import ReflexesAdminPage from "./pages/admin/medical-reference/ReflexesAdminPage";
import StainProvidersAdminPage from "./pages/admin/medical-reference/StainProvidersAdminPage";
import GrossCodesAdminPage from "./pages/admin/medical-reference/GrossCodesAdminPage";
import InternalGrossCodesPage from "./pages/admin/medical-reference/InternalGrossCodesPage";
import CptCodesAdminPage from "./pages/admin/medical-reference/CptCodesAdminPage";
import CptModifiersAdminPage from "./pages/admin/medical-reference/CptModifiersAdminPage";
import IcdCodesAdminPage from "./pages/admin/medical-reference/IcdCodesAdminPage";
import DiagnosisCategoriesAdminPage from "./pages/admin/medical-reference/DiagnosisCategoriesAdminPage";
import DiagnosisAdminPage from "./pages/admin/medical-reference/DiagnosisAdminPage";
import AbbreviationsAdminPage from "./pages/admin/medical-reference/AbbreviationsAdminPage";
import AddendumMacrosAdminPage from "./pages/admin/medical-reference/AddendumMacrosAdminPage";
import SpecimenMacrosAdminPage from "./pages/admin/medical-reference/SpecimenMacrosAdminPage";
import ErrorsAdminPage from "./pages/admin/quality-control/ErrorsAdminPage";
import CorrectionsAdminPage from "./pages/admin/quality-control/CorrectionsAdminPage";
import PhysiciansAdminPage from "./pages/admin/clients/PhysiciansAdminPage";
import PracticesAdminPage from "./pages/admin/clients/PracticesAdminPage";
import SurgeryCentersLabsAdminPage from "./pages/admin/clients/SurgeryCentersLabsAdminPage";
import SalesRepsAdminPage from "./pages/admin/clients/SalesRepsAdminPage";
import PortalAccessAdminPage from "./pages/admin/portal-access/PortalAccessAdminPage";
import ReportDeliveryMethodsAdminPage from "./pages/admin/report-delivery/ReportDeliveryMethodsAdminPage";
import AccountReportDeliveryAdminPage from "./pages/admin/report-delivery/AccountReportDeliveryAdminPage";
import PhysicianReportDeliveryAdminPage from "./pages/admin/report-delivery/PhysicianReportDeliveryAdminPage";
import SurgeryCenterReportDeliveryAdminPage from "./pages/admin/report-delivery/SurgeryCenterReportDeliveryAdminPage";
import InsuranceProvidersAdminPage from "./pages/admin/billing/InsuranceProvidersAdminPage";
import InsurancePlanTypesAdminPage from "./pages/admin/billing/InsurancePlanTypesAdminPage";
import InsuranceProviderPlanTypesAdminPage from "./pages/admin/billing/InsuranceProviderPlanTypesAdminPage";
import BillingScenariosAdminPage from "./pages/admin/billing/BillingScenariosAdminPage";
import PrefixConfigurationAdminPage from "./pages/admin/billing/PrefixConfigurationAdminPage";
import GlobalInsuranceEligibilityPage from "./pages/admin/billing/GlobalInsuranceEligibilityPage";
import BillingDashboardPage from "./pages/billing/BillingDashboardPage";
import ReportsLandingPage from "./pages/reports/ReportsLandingPage";
import WorkloadReportPage from "./pages/reports/WorkloadReportPage";
import CptFrequencyReportPage from "./pages/reports/CptFrequencyReportPage";
import SpecimenLogReportPage from "./pages/reports/SpecimenLogReportPage";
import AccessionLogReportPage from "./pages/reports/AccessionLogReportPage";
import CancerCaseReportPage from "./pages/reports/CancerCaseReportPage";
import BillingStatusReportPage from "./pages/reports/BillingStatusReportPage";
import PayablesReportPage from "./pages/reports/PayablesReportPage";
import ManualTebraEntryReportPage from "./pages/reports/ManualTebraEntryReportPage";
import LabPaymentAgreementsAdminPage from "./pages/admin/lab-payment-agreements/LabPaymentAgreementsAdminPage";
import VendorsAdminPage from "./pages/admin/staining/VendorsAdminPage";
import MachinesAdminPage from "./pages/admin/staining/MachinesAdminPage";
import MachineCategoriesAdminPage from "./pages/admin/equipment/MachineCategoriesAdminPage";

// Compliance Admin Pages
import AuditLogPage from "./pages/admin/AuditLogPage";
import CommunicationLogsPage from "./pages/admin/CommunicationLogsPage";
import SystemEmailsPage from "./pages/admin/SystemEmailsPage";
import MyActivityPage from "./pages/MyActivityPage";
import { PermissionGuard } from "./components/common/PermissionGuard";

// Send-Outs Admin Pages
import ExternalInstitutionsAdminPage from "./pages/admin/send-outs/ExternalInstitutionsAdminPage";

// Task Admin Pages
import RoleTasksAdminPage from "./pages/admin/organization/RoleTasksAdminPage";
import GroupTasksAdminPage from "./pages/admin/organization/GroupTasksAdminPage";
import TasksAdminPage from "./pages/admin/organization/TasksAdminPage";

// Case Flags Admin Page
import FlagsAdminPage from "./pages/admin/medical-reference/FlagsAdminPage";

// Workflow Configuration Admin Pages
import CaseStatusesAdminPage from "./pages/admin/workflow/CaseStatusesAdminPage";
import WorkflowsAdminPage from "./pages/admin/workflow/WorkflowsAdminPage";

// Report Configuration Admin Pages
import ReportTemplatesAdminPage from "./pages/admin/report-configuration/ReportTemplatesAdminPage";
import OrganMapTemplatesAdminPage from "./pages/admin/report-configuration/OrganMapTemplatesAdminPage";
import ReportReviewSettingsAdminPage from "./pages/admin/report-configuration/ReportReviewSettingsAdminPage";

// Session Recording
import SessionRecordingsPage from "./pages/admin/SessionRecordingsPage";
import CaseLocksPage from "./pages/admin/CaseLocksPage";
import BridgeAuditQueryPage from "./pages/admin/BridgeAuditQueryPage";

// AI Extraction
import AiExtractionDashboardPage from "./pages/admin/AiExtractionDashboardPage";
import { isAiFeedbackEnabled } from "./utils/aiFeedbackFlag";

// Observations Admin Pages
import ObservationsListPage from "./pages/admin/observations/ObservationsListPage";
import ObservationDetailPage from "./pages/admin/observations/ObservationDetailPage";
import MyObservationsListPage from "./pages/observations/MyObservationsListPage";
import MyObservationDetailPage from "./pages/observations/MyObservationDetailPage";

// Bridge Admin Pages
import BridgeWorkstationList from "./pages/admin/bridge/BridgeWorkstationList";
import SlidePrintRoutingSettingsAdminPage from "./pages/admin/bridge/SlidePrintRoutingSettingsAdminPage";
import BridgeWorkstationDetail from "./pages/admin/bridge/BridgeWorkstationDetail";
import BridgeDeviceEventLog from "./pages/admin/bridge/BridgeDeviceEventLog";

// Workflow pages
import {
  AccessioningPage,
  GrossingQueuePage,
  GrossingDetailPage,
  EmbeddingQueuePage,
  EmbeddingDetailPage,
  CuttingQueuePage,
  CuttingDetailPage,
  ControlSlidesPage,
  StainingQueuePage,
  DistributionPage,
  PathologistWorkQueuePage,
  DiscussionQueuePage,
  CaseDiscussionPage,
  QaReviewQueuePage,
  QaMetricsPage,
  StainMetricsPage,
  ShippingQueuePage,
  SendOutQueuePage,
  ErrorResolutionPage,
  ReportDeliveryQueuePage,
  CorrectionsQueuePage,
} from "./pages/workflows";
import PathologistReviewDetailRoute from "./pages/workflows/PathologistReviewDetailRoute";
import WorkListsPage from "./pages/workflows/WorkListsPage";
import PendingWorkQueuePage from "./pages/workflows/PendingWorkQueuePage";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Protected route component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
        role="status"
        aria-label="Loading"
      >
        <Loader size="md" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public route component (redirects to the appropriate landing page if authenticated and tasks selected)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();
  const { hasSelectedTasks, selectedTasks } = useTaskStore();

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
        role="status"
        aria-label="Loading"
      >
        <Loader size="md" />
      </div>
    );
  }

  if (isAuthenticated) {
    const destination = hasSelectedTasks
      ? getDefaultRoute(selectedTasks.map((t) => t.slug))
      : "/select-tasks";
    return <Navigate to={destination} replace />;
  }

  return <>{children}</>;
}

function PortalProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = usePortalAuthStore();
  if (!isAuthenticated) {
    return <Navigate to="/portal/login" replace />;
  }
  return <>{children}</>;
}

// Pending Work is read-only and available before clinical tasks are chosen.
function TaskSelectionGuard({ children }: { children: React.ReactNode }) {
  const { hasSelectedTasks } = useTaskStore();
  const { pathname } = useLocation();
  if (!hasSelectedTasks && pathname !== "/pending-work") {
    return <Navigate to="/select-tasks" replace />;
  }
  return <>{children}</>;
}

// Blocks direct URL navigation to routes the user's selected tasks don't allow
function RoutePermissionGuard({ children }: { children: React.ReactNode }) {
  const { selectedTasks } = useTaskStore();
  const profile = useAuthStore((state) => state.profile);
  const location = useLocation();
  const selectedSlugs = selectedTasks.map((task) => task.slug);

  const allowedLegacyRoute =
    (location.pathname.startsWith("/admin/legacy-cases/reports/") &&
      canReviewLegacyReports(profile)) ||
    ((location.pathname === "/admin/legacy-cases" ||
      location.pathname.startsWith("/admin/legacy-cases/")) &&
      canManageLegacyCases(profile));
  if (
    !allowedLegacyRoute &&
    !isRouteAllowed(location.pathname, selectedSlugs)
  ) {
    return <Navigate to={getDefaultRoute(selectedSlugs)} replace />;
  }

  return <>{children}</>;
}

// Renders AppLayout and tracks page visits for authenticated users
function AuthenticatedLayout() {
  usePageVisitTracking();
  return <AppLayout />;
}

// Helper function to wrap page components with ErrorBoundary
function withErrorBoundary(Component: ComponentType) {
  return (
    <ErrorBoundary>
      <Component />
    </ErrorBoundary>
  );
}

function SessionTimeoutWrapper() {
  const { showWarning, remainingSeconds, extendSession, performLogout } =
    useSessionTimeout();
  return (
    <SessionTimeoutModal
      opened={showWarning}
      remainingSeconds={remainingSeconds}
      onExtend={extendSession}
      onLogout={performLogout}
    />
  );
}

function isNonStaffObservationRoute(pathname: string) {
  return (
    pathname === "/" ||
    pathname === "/login" ||
    pathname === "/forgot-password" ||
    pathname === "/reset-password" ||
    pathname === "/select-portal" ||
    pathname === "/portal" ||
    pathname.startsWith("/portal/") ||
    pathname.startsWith("/report/portal/") ||
    pathname.startsWith("/internal/")
  );
}

function StaffObservationOverlay({
  sessionId,
  recordingHook,
}: {
  sessionId: string | null;
  recordingHook: UseObsScreenRecordingResult;
}) {
  const { isAuthenticated, isLoading } = useAuthStore();
  const { pathname } = useLocation();
  const enabled =
    !isLoading && isAuthenticated && !isNonStaffObservationRoute(pathname);

  useEffect(() => {
    if (enabled || recordingHook.state === "idle") return;

    if (recordingHook.state === "error" || recordingHook.state === "canceled") {
      recordingHook.reset();
      return;
    }

    // Never let an in-progress staff screen capture continue after logout or
    // navigation into a public/physician-portal route.
    recordingHook.cancelRecording();
  }, [enabled, recordingHook]);

  if (!enabled) return null;

  return (
    <>
      <FloatingObservationTrigger
        sessionId={sessionId}
        recordingHook={recordingHook}
      />
      <ObservationRecorder recordingHook={recordingHook} />
    </>
  );
}

function App() {
  const { token, setLoading } = useAuthStore();
  const { sessionId } = useGcpSessionRecording();
  const observationRecordingHook = useObsScreenRecording();

  useEffect(() => {
    // Restore auth token from storage on app load
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      setAuthToken(storedToken);
    }
    setLoading(false);
  }, [setLoading]);

  // Set auth token in API client whenever it changes
  useEffect(() => {
    if (token) {
      setAuthToken(token);
    }
  }, [token]);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <StaffObservationOverlay
        sessionId={sessionId ?? null}
        recordingHook={observationRecordingHook}
      />
      <SessionTimeoutWrapper />
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          }
        />

        {/* Force Change Password (authenticated but no layout) */}
        <Route
          path="/change-password"
          element={
            <ProtectedRoute>
              <ForceChangePassword />
            </ProtectedRoute>
          }
        />

        {/* Task Selection (authenticated but no layout) */}
        <Route
          path="/select-tasks"
          element={
            <ProtectedRoute>
              <TaskSelection />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes with AppLayout */}
        <Route
          element={
            <ProtectedRoute>
              <TaskSelectionGuard>
                <AuthenticatedLayout />
              </TaskSelectionGuard>
            </ProtectedRoute>
          }
        >
          <Route
            path="/dashboard"
            element={
              <RoutePermissionGuard>
                {withErrorBoundary(Dashboard)}
              </RoutePermissionGuard>
            }
          />
          <Route
            path="/account/settings"
            element={withErrorBoundary(AccountSettings)}
          />
          <Route
            path="/my-activity"
            element={withErrorBoundary(MyActivityPage)}
          />
          <Route
            path="/accessioning"
            element={
              <TaskGuard>{withErrorBoundary(AccessioningPage)}</TaskGuard>
            }
          />
          <Route
            path="/accessioning/:caseId"
            element={
              <TaskGuard>{withErrorBoundary(AccessioningPage)}</TaskGuard>
            }
          />
          <Route path="/cases" element={withErrorBoundary(CasesList)} />
          <Route path="/cases/:id" element={withErrorBoundary(CaseDetailV2)} />
          <Route element={<LegacyReportReviewGuard />}>
            <Route
              path="/admin/legacy-cases/reports/:caseId"
              element={withErrorBoundary(LegacyReportReviewPage)}
            />
          </Route>
          <Route element={<LegacyCasesGuard />}>
            <Route
              path="/admin/legacy-cases"
              element={withErrorBoundary(LegacyCasesPage)}
            />
            <Route
              path="/admin/legacy-cases/:id"
              element={withErrorBoundary(LegacyCaseDetailPage)}
            />
          </Route>
          <Route
            path="/my/observations"
            element={withErrorBoundary(MyObservationsListPage)}
          />
          <Route
            path="/my/observations/:id"
            element={withErrorBoundary(MyObservationDetailPage)}
          />
          <Route
            path="/grossing"
            element={
              <TaskGuard>{withErrorBoundary(GrossingQueuePage)}</TaskGuard>
            }
          />
          <Route
            path="/grossing/:caseId"
            element={
              <TaskGuard>{withErrorBoundary(GrossingDetailPage)}</TaskGuard>
            }
          />
          <Route
            path="/embedding"
            element={
              <TaskGuard>{withErrorBoundary(EmbeddingQueuePage)}</TaskGuard>
            }
          />
          <Route
            path="/embedding/:caseId"
            element={
              <TaskGuard>{withErrorBoundary(EmbeddingDetailPage)}</TaskGuard>
            }
          />
          <Route
            path="/cutting"
            element={
              <TaskGuard>{withErrorBoundary(CuttingQueuePage)}</TaskGuard>
            }
          />
          <Route
            path="/distribution/control-slides"
            element={
              <TaskGuard>{withErrorBoundary(ControlSlidesPage)}</TaskGuard>
            }
          />
          <Route
            path="/cutting/:caseId"
            element={
              <TaskGuard>{withErrorBoundary(CuttingDetailPage)}</TaskGuard>
            }
          />
          <Route
            path="/staining"
            element={
              <TaskGuard>{withErrorBoundary(StainingQueuePage)}</TaskGuard>
            }
          />
          <Route
            path="/distribution"
            element={
              <TaskGuard>{withErrorBoundary(DistributionPage)}</TaskGuard>
            }
          />
          <Route
            path="/pending-work"
            element={
              <TaskGuard>{withErrorBoundary(PendingWorkQueuePage)}</TaskGuard>
            }
          />
          <Route
            path="/pathologist-review"
            element={
              <TaskGuard>
                {withErrorBoundary(PathologistWorkQueuePage)}
              </TaskGuard>
            }
          />
          <Route
            path="/pathologist-review/:caseId"
            element={
              <TaskGuard>
                {withErrorBoundary(PathologistReviewDetailRoute)}
              </TaskGuard>
            }
          />
          <Route
            path="/discussion-queue"
            element={
              <TaskGuard>{withErrorBoundary(DiscussionQueuePage)}</TaskGuard>
            }
          />
          <Route
            path="/discussion-queue/:caseId/:discussionId?"
            element={
              <TaskGuard>{withErrorBoundary(CaseDiscussionPage)}</TaskGuard>
            }
          />
          <Route
            path="/qa-review"
            element={
              <TaskGuard>{withErrorBoundary(QaReviewQueuePage)}</TaskGuard>
            }
          />
          <Route
            path="/qa-metrics"
            element={<TaskGuard>{withErrorBoundary(QaMetricsPage)}</TaskGuard>}
          />
          <Route
            path="/stain-metrics"
            element={
              <TaskGuard>{withErrorBoundary(StainMetricsPage)}</TaskGuard>
            }
          />
          <Route
            path="/shipping"
            element={
              <TaskGuard>{withErrorBoundary(ShippingQueuePage)}</TaskGuard>
            }
          />
          <Route
            path="/send-outs"
            element={
              <TaskGuard>{withErrorBoundary(SendOutQueuePage)}</TaskGuard>
            }
          />
          <Route
            path="/report-delivery"
            element={
              <TaskGuard>
                {withErrorBoundary(ReportDeliveryQueuePage)}
              </TaskGuard>
            }
          />
          <Route
            path="/corrections-queue"
            element={
              <TaskGuard>{withErrorBoundary(CorrectionsQueuePage)}</TaskGuard>
            }
          />
          <Route
            path="/errors"
            element={withErrorBoundary(ErrorResolutionPage)}
          />
          <Route
            path="/work-lists"
            element={withErrorBoundary(WorkListsPage)}
          />

          {/* Billing */}
          <Route
            path="/billing"
            element={withErrorBoundary(BillingDashboardPage)}
          />

          {/* Case Locks — permission-gated, visible outside admin */}
          <Route
            path="/case-locks"
            element={withErrorBoundary(CaseLocksPage)}
          />

          {/* Reports Hub */}
          <Route
            path="/reports"
            element={withErrorBoundary(ReportsLandingPage)}
          />
          <Route
            path="/reports/workload"
            element={withErrorBoundary(WorkloadReportPage)}
          />
          <Route
            path="/reports/cpt-frequency"
            element={withErrorBoundary(CptFrequencyReportPage)}
          />
          <Route
            path="/reports/specimen-log"
            element={withErrorBoundary(SpecimenLogReportPage)}
          />
          <Route
            path="/reports/accession-log"
            element={withErrorBoundary(AccessionLogReportPage)}
          />
          <Route
            path="/reports/cancer-cases"
            element={withErrorBoundary(CancerCaseReportPage)}
          />
          <Route
            path="/reports/billing-status"
            element={withErrorBoundary(BillingStatusReportPage)}
          />
          <Route
            path="/reports/payables"
            element={withErrorBoundary(PayablesReportPage)}
          />
          <Route
            path="/reports/manual-tebra-entry"
            element={withErrorBoundary(ManualTebraEntryReportPage)}
          />

          {/* Admin Routes — wrapped with TaskGuard to enforce "admin" task selection */}
          <Route element={<TaskGuard />}>
            <Route path="/admin" element={withErrorBoundary(AdminPage)} />
            <Route
              path="/admin/facilities"
              element={withErrorBoundary(FacilitiesAdminPage)}
            />
            <Route
              path="/admin/users"
              element={withErrorBoundary(UsersAdminPage)}
            />
            <Route
              path="/admin/roles"
              element={withErrorBoundary(RolesAdminPage)}
            />
            <Route
              path="/admin/groups"
              element={withErrorBoundary(GroupsAdminPage)}
            />
            <Route
              path="/admin/permissions"
              element={withErrorBoundary(PermissionsAdminPage)}
            />
            <Route
              path="/admin/group-permissions"
              element={withErrorBoundary(GroupPermissionsAdminPage)}
            />
            <Route
              path="/admin/role-permissions"
              element={withErrorBoundary(RolePermissionsAdminPage)}
            />
            <Route
              path="/admin/procedures"
              element={withErrorBoundary(ProceduresAdminPage)}
            />
            <Route
              path="/admin/collection-methods"
              element={withErrorBoundary(CollectionMethodsAdminPage)}
            />
            <Route
              path="/admin/sources"
              element={withErrorBoundary(SourcesAdminPage)}
            />
            <Route
              path="/admin/source-subsites"
              element={withErrorBoundary(SourceSubsitesAdminPage)}
            />
            <Route
              path="/admin/source-default-stains"
              element={withErrorBoundary(SourceDefaultStainsAdminPage)}
            />
            <Route
              path="/admin/stains"
              element={withErrorBoundary(StainsAdminPage)}
            />
            <Route
              path="/admin/panels"
              element={
                <PermissionGuard
                  permissions={["panels.manage"]}
                  fallback={<Navigate to="/admin" replace />}
                >
                  {withErrorBoundary(PanelsAdminPage)}
                </PermissionGuard>
              }
            />
            <Route
              path="/admin/reflexes"
              element={
                <PermissionGuard
                  permissions={["panels.manage"]}
                  fallback={<Navigate to="/admin" replace />}
                >
                  {withErrorBoundary(ReflexesAdminPage)}
                </PermissionGuard>
              }
            />
            <Route
              path="/admin/stain-providers"
              element={withErrorBoundary(StainProvidersAdminPage)}
            />
            <Route
              path="/admin/accessioning-gross-codes"
              element={withErrorBoundary(GrossCodesAdminPage)}
            />
            <Route
              path="/admin/internal-gross-codes"
              element={withErrorBoundary(InternalGrossCodesPage)}
            />
            <Route
              path="/admin/cpt-codes"
              element={withErrorBoundary(CptCodesAdminPage)}
            />
            <Route
              path="/admin/cpt-modifiers"
              element={withErrorBoundary(CptModifiersAdminPage)}
            />
            <Route
              path="/admin/icd-codes"
              element={withErrorBoundary(IcdCodesAdminPage)}
            />
            <Route
              path="/admin/diagnosis-categories"
              element={withErrorBoundary(DiagnosisCategoriesAdminPage)}
            />
            <Route
              path="/admin/diagnosis"
              element={withErrorBoundary(DiagnosisAdminPage)}
            />
            <Route
              path="/admin/abbreviations"
              element={withErrorBoundary(AbbreviationsAdminPage)}
            />
            <Route
              path="/admin/addendum-macros"
              element={withErrorBoundary(AddendumMacrosAdminPage)}
            />
            <Route
              path="/admin/specimen-macros"
              element={withErrorBoundary(SpecimenMacrosAdminPage)}
            />
            <Route
              path="/admin/errors"
              element={withErrorBoundary(ErrorsAdminPage)}
            />
            <Route
              path="/admin/corrections"
              element={withErrorBoundary(CorrectionsAdminPage)}
            />
            <Route
              path="/admin/physicians"
              element={withErrorBoundary(PhysiciansAdminPage)}
            />
            <Route
              path="/admin/practices"
              element={withErrorBoundary(PracticesAdminPage)}
            />
            <Route
              path="/admin/surgery-centers-labs"
              element={withErrorBoundary(SurgeryCentersLabsAdminPage)}
            />
            <Route
              path="/admin/sales-reps"
              element={withErrorBoundary(SalesRepsAdminPage)}
            />
            <Route
              path="/admin/portal-access"
              element={withErrorBoundary(PortalAccessAdminPage)}
            />
            <Route
              path="/admin/report-delivery-methods"
              element={withErrorBoundary(ReportDeliveryMethodsAdminPage)}
            />
            <Route
              path="/admin/account-report-delivery"
              element={withErrorBoundary(AccountReportDeliveryAdminPage)}
            />
            <Route
              path="/admin/physician-report-delivery"
              element={withErrorBoundary(PhysicianReportDeliveryAdminPage)}
            />
            <Route
              path="/admin/surgery-center-report-delivery"
              element={withErrorBoundary(SurgeryCenterReportDeliveryAdminPage)}
            />
            <Route
              path="/admin/insurance-providers"
              element={withErrorBoundary(InsuranceProvidersAdminPage)}
            />
            <Route
              path="/admin/insurance-plan-types"
              element={withErrorBoundary(InsurancePlanTypesAdminPage)}
            />
            <Route
              path="/admin/insurance-provider-plan-types"
              element={withErrorBoundary(InsuranceProviderPlanTypesAdminPage)}
            />
            <Route
              path="/admin/billing-scenarios"
              element={withErrorBoundary(BillingScenariosAdminPage)}
            />
            <Route
              path="/admin/billing/prefix-configuration"
              element={withErrorBoundary(PrefixConfigurationAdminPage)}
            />
            <Route
              path="/admin/billing/global-insurance"
              element={withErrorBoundary(GlobalInsuranceEligibilityPage)}
            />
            <Route
              path="/admin/lab-payment-agreements"
              element={withErrorBoundary(LabPaymentAgreementsAdminPage)}
            />
            <Route
              path="/admin/vendors"
              element={withErrorBoundary(VendorsAdminPage)}
            />
            <Route
              path="/admin/machines"
              element={withErrorBoundary(MachinesAdminPage)}
            />
            <Route
              path="/admin/machine-categories"
              element={withErrorBoundary(MachineCategoriesAdminPage)}
            />

            {/* Case Flags */}
            <Route
              path="/admin/case-flags"
              element={withErrorBoundary(FlagsAdminPage)}
            />

            {/* Workflow Configuration */}
            <Route
              path="/admin/case-statuses"
              element={withErrorBoundary(CaseStatusesAdminPage)}
            />
            <Route
              path="/admin/workflows"
              element={withErrorBoundary(WorkflowsAdminPage)}
            />

            {/* Bridge Admin */}
            <Route
              path="/admin/bridge"
              element={withErrorBoundary(BridgeWorkstationList)}
            />
            <Route
              path="/admin/slide-print-settings"
              element={withErrorBoundary(SlidePrintRoutingSettingsAdminPage)}
            />
            <Route
              path="/admin/bridge/:id"
              element={withErrorBoundary(BridgeWorkstationDetail)}
            />
            <Route
              path="/admin/bridge/:id/events"
              element={withErrorBoundary(BridgeDeviceEventLog)}
            />

            {/* Report Configuration Admin */}
            <Route
              path="/admin/report-templates"
              element={withErrorBoundary(ReportTemplatesAdminPage)}
            />
            <Route
              path="/admin/organ-map-templates"
              element={withErrorBoundary(OrganMapTemplatesAdminPage)}
            />
            <Route
              path="/admin/report-review-settings"
              element={withErrorBoundary(ReportReviewSettingsAdminPage)}
            />

            {/* Send-Outs Admin */}
            <Route
              path="/admin/external-institutions"
              element={withErrorBoundary(ExternalInstitutionsAdminPage)}
            />

            {/* Task Admin */}
            <Route
              path="/admin/tasks"
              element={withErrorBoundary(TasksAdminPage)}
            />
            <Route
              path="/admin/role-tasks"
              element={withErrorBoundary(RoleTasksAdminPage)}
            />
            <Route
              path="/admin/group-tasks"
              element={withErrorBoundary(GroupTasksAdminPage)}
            />

            {/* Bridge Device Audit (unified across stickers, slides, cassettes, scans) */}
            <Route
              path="/admin/sticker-audit"
              element={withErrorBoundary(BridgeAuditQueryPage)}
            />
            <Route
              path="/admin/bridge-audit"
              element={withErrorBoundary(BridgeAuditQueryPage)}
            />

            {/* Compliance Admin */}
            <Route
              path="/admin/audit-logs"
              element={withErrorBoundary(AuditLogPage)}
            />
            <Route
              path="/admin/communication-logs"
              element={withErrorBoundary(CommunicationLogsPage)}
            />
            <Route
              path="/admin/system-emails"
              element={
                <PermissionGuard
                  permissions={["system_emails.read"]}
                  fallback={<Navigate to="/admin" replace />}
                >
                  {withErrorBoundary(SystemEmailsPage)}
                </PermissionGuard>
              }
            />

            {/* Session Recordings */}
            <Route
              path="/admin/session-recordings"
              element={withErrorBoundary(SessionRecordingsPage)}
            />

            {/* AI Extraction Dashboard — production only */}
            {isAiFeedbackEnabled() && (
              <Route
                path="/admin/ai-extraction"
                element={withErrorBoundary(AiExtractionDashboardPage)}
              />
            )}

            {/* Observations (ESA only) */}
            <Route
              path="/admin/observations"
              element={withErrorBoundary(ObservationsListPage)}
            />

            <Route
              path="/admin/observations/:id"
              element={withErrorBoundary(ObservationDetailPage)}
            />
          </Route>
        </Route>

        {/* Public portal report viewer — no auth required, token-gated */}
        <Route path="/report/portal/:token" element={<PortalReportView />} />

        {/* Internal render route used by the backend Puppeteer PDF renderer.
            Bootstrapped from a short-lived fragment token and authenticated by
            a scoped API header, not a user session. */}
        <Route
          path="/internal/render-report/:signOffId"
          element={<RenderReportPage />}
        />
        <Route
          path="/internal/render-legacy-reconstruction/:reconstructionId"
          element={<RenderReportPage />}
        />
        <Route
          path="/internal/render-billing-report/:reportType"
          element={<RenderBillingReportPage />}
        />
        <Route
          path="/internal/render-tc-technical-summary/:caseId"
          element={<RenderTcTechnicalSummaryPage />}
        />

        {/* Physician Portal — separate public section, no LIS auth required */}
        <Route element={<PortalLayout />}>
          {/* Portal routes are added in phases: T053 (register), T066 (login/dashboard/report), T084 (account) */}
          <Route
            path="/portal"
            element={<Navigate to="/portal/login" replace />}
          />
          <Route path="/portal/register" element={<PortalRegisterPage />} />
          <Route path="/portal/login" element={<PortalLoginPage />} />
          <Route
            path="/portal/reset-password"
            element={<PortalPasswordResetPage />}
          />
          <Route path="/portal/access" element={<PortalMagicAccessPage />} />
          <Route
            path="/portal/dashboard"
            element={
              <PortalProtectedRoute>
                <PortalDashboardPage />
              </PortalProtectedRoute>
            }
          />
          <Route
            path="/portal/report/:caseId"
            element={
              <PortalProtectedRoute>
                <PortalReportViewPage />
              </PortalProtectedRoute>
            }
          />
          <Route
            path="/portal/account"
            element={
              <PortalProtectedRoute>
                <PortalAccountPage />
              </PortalProtectedRoute>
            }
          />
          <Route
            path="/portal/request-supplies"
            element={
              <PortalProtectedRoute>
                <PortalRequestSuppliesPage />
              </PortalProtectedRoute>
            }
          />
          <Route
            path="/portal/help"
            element={
              <PortalProtectedRoute>
                <PortalContactHelpPage />
              </PortalProtectedRoute>
            }
          />
        </Route>

        {/* Portal selector */}
        <Route path="/select-portal" element={<PortalSelector />} />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
